import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { TableTabs, type TableTab } from '@/components/common/TableTabs';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { expenseStatementQueryOptions } from '../api/zonalStatement.api';
import { getExpenseDayColumns, getExpenseTypeColumns } from '../components/StatementColumns';
import { getExpenseStatCards } from '../utils/statementStatCards';
import { getStatementTitle } from '../utils/statementTitle';

const route = getRouteApi('/_auth/dashboards/zonal-statement/$userId/expense');

const STATEMENT_NAME = 'Expense Statement';
const ERROR_TITLE = 'Could not load the expense statement';

type ExpenseStatementTab = 'days' | 'types';

interface ExpenseStatementPageProps {
  userId: number;
}

export function ExpenseStatementPage({ userId }: ExpenseStatementPageProps) {
  const search = route.useSearch();
  const navigate = route.useNavigate();
  const [activeTab, setActiveTab] = useState<ExpenseStatementTab>('days');

  useRememberViewedRow('zonalStatementZms', userId);

  const statementQuery = useQuery(
    expenseStatementQueryOptions(userId, { dateFrom: search.dateFrom, dateTo: search.dateTo }),
  );
  const statement = statementQuery.data;
  const dayColumns = useMemo(() => getExpenseDayColumns(), []);
  const typeColumns = useMemo(() => getExpenseTypeColumns(), []);

  const tabs: TableTab<ExpenseStatementTab>[] = [
    { id: 'days', label: 'Day by Day', count: statement?.days.length },
    { id: 'types', label: 'By Expense Type', count: statement?.byExpenseType.length },
  ];

  const sharedLayout = {
    title: statementQuery.isLoading
      ? undefined
      : getStatementTitle(STATEMENT_NAME, statement?.member.name),
    titleAddon: (
      <DateRangeBadge
        dateFrom={search.dateFrom ?? statement?.period.dateFrom}
        dateTo={search.dateTo ?? statement?.period.dateTo}
      />
    ),
    onNoHistory: () => void navigate({ to: '/dashboards/zonal-statement', search }),
    stats: getExpenseStatCards(statement?.totals),
    tableTabs: (
      <TableTabs
        tabs={tabs}
        activeTabId={activeTab}
        onTabChange={setActiveTab}
        ariaLabel="Expense statement tables"
      />
    ),
  };
  const sharedTable = {
    isLoading: statementQuery.isLoading,
    error: statementQuery.error,
    errorTitle: ERROR_TITLE,
    onRetry: () => void statementQuery.refetch(),
  };

  if (activeTab === 'types') {
    return (
      <DetailTableLayout
        {...sharedLayout}
        table={{
          ...sharedTable,
          rows: statement?.byExpenseType ?? [],
          columns: typeColumns,
          stickyColumnId: 'name',
          emptyMessage: 'No approved expenses in this period',
        }}
      />
    );
  }

  return (
    <DetailTableLayout
      {...sharedLayout}
      table={{
        ...sharedTable,
        rows: statement?.days ?? [],
        columns: dayColumns,
        stickyColumnId: 'date',
        emptyMessage: 'No days in this period',
        getRowClassName: (day) => (day.total === 0 ? 'text-muted-foreground' : undefined),
      }}
    />
  );
}
